import React, { useCallback, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useUserController } from "../../../../context/user";
import matrixApi from "./matrixApi";
import TrainingMatrixTab from "./TrainingMatrixTab";
import JobCategoryMatrixTab from "./JobCategoryMatrixTab";
import {
  dedupeSortedStrings,
  normalizeJobCategoryRows,
  normalizeTrainingRows,
  triggerDownloadFromResponse,
} from "./utils";

const mergeObjectsById = (left = [], right = []) => {
  const map = new Map();
  [...left, ...right].forEach((item) => {
    if (!item || !item.id) return;
    map.set(item.id, item);
  });
  return [...map.values()].sort((a, b) =>
    (a.title || a.id || "").localeCompare(b.title || b.id || "")
  );
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.response?.data?.details) {
    return `${error.response.data.message}. ${JSON.stringify(error.response.data.details)}`;
  }
  return error?.response?.data?.message || error?.message || fallback;
};

function MatrixBuilderPanel() {
  const [userController] = useUserController();
  const { user } = userController;

  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("training");
  const [loading, setLoading] = useState(false);
  const [savingTraining, setSavingTraining] = useState(false);
  const [savingJobCategories, setSavingJobCategories] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [trainingRows, setTrainingRows] = useState([]);
  const [jobCategoryRows, setJobCategoryRows] = useState([]);
  const [lookups, setLookups] = useState({
    quizzes: [],
    categories: [],
    jobClassifications: [],
    jobNames: [],
  });

  const isAdminPlus = !!user && user.Admin === "Admin+";
  const actorEmail = user?.email || "";

  const loadMatrixData = useCallback(async () => {
    setLoading(true);
    setError("");
    setStatus("");

    try {
      await matrixApi.bootstrap();

      const [trainingResponse, jobCategoryResponse] = await Promise.all([
        matrixApi.getTraining(),
        matrixApi.getJobCategories(),
      ]);

      const trainingPayload = trainingResponse?.data || {};
      const jobCategoryPayload = jobCategoryResponse?.data || {};

      const normalizedTrainingRows = normalizeTrainingRows(trainingPayload?.rows || []);
      const normalizedJobCategoryRows = normalizeJobCategoryRows(jobCategoryPayload?.rows || []);

      setTrainingRows(normalizedTrainingRows);
      setJobCategoryRows(normalizedJobCategoryRows);
      setLookups({
        quizzes: trainingPayload?.lookups?.quizzes || [],
        categories: mergeObjectsById(
          trainingPayload?.lookups?.categories || [],
          jobCategoryPayload?.lookups?.categories || []
        ),
        jobClassifications: dedupeSortedStrings(trainingPayload?.lookups?.jobClassifications || []),
        jobNames: dedupeSortedStrings(jobCategoryPayload?.lookups?.jobNames || []),
      });
      setInitialized(true);
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Failed to load matrix data."));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExpandToggle = async () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (nextExpanded && !initialized) {
      await loadMatrixData();
    }
  };

  const saveTrainingMatrix = async (rows) => {
    setSavingTraining(true);
    setError("");
    setStatus("");
    try {
      const payload = {
        rows: normalizeTrainingRows(rows),
        actorEmail,
      };
      const response = await matrixApi.saveTraining(payload);
      const savedRows = normalizeTrainingRows(response?.data?.rows || payload.rows);
      setTrainingRows(savedRows);
      setStatus(`Saved ${savedRows.length} training matrix row(s).`);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Failed to save training matrix."));
    } finally {
      setSavingTraining(false);
    }
  };

  const saveJobCategoryMatrix = async (rows) => {
    setSavingJobCategories(true);
    setError("");
    setStatus("");
    try {
      const payload = {
        rows: normalizeJobCategoryRows(rows),
        actorEmail,
      };
      const response = await matrixApi.saveJobCategories(payload);
      const savedRows = normalizeJobCategoryRows(response?.data?.rows || payload.rows);
      setJobCategoryRows(savedRows);
      setStatus(`Saved ${savedRows.length} job category matrix row(s).`);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Failed to save job category matrix."));
    } finally {
      setSavingJobCategories(false);
    }
  };

  const handleExport = async (type) => {
    setError("");
    setStatus("");
    try {
      const response = await matrixApi.exportCsv(type);
      const fallbackName = type === "training" ? "training-matrix.csv" : "job-category-matrix.csv";
      triggerDownloadFromResponse(response, fallbackName);
      setStatus("CSV export complete.");
    } catch (exportError) {
      setError(getErrorMessage(exportError, "Failed to export matrix CSV."));
    }
  };

  const tabContent = useMemo(() => {
    if (activeTab === "training") {
      return (
        <TrainingMatrixTab
          rows={trainingRows}
          setRows={setTrainingRows}
          quizzes={lookups.quizzes}
          categories={lookups.categories}
          jobClassifications={lookups.jobClassifications}
          onSave={saveTrainingMatrix}
          onExport={() => handleExport("training")}
          saving={savingTraining}
        />
      );
    }

    return (
      <JobCategoryMatrixTab
        rows={jobCategoryRows}
        setRows={setJobCategoryRows}
        categories={lookups.categories}
        jobNames={lookups.jobNames}
        onSave={saveJobCategoryMatrix}
        onExport={() => handleExport("job-categories")}
        saving={savingJobCategories}
      />
    );
  }, [
    activeTab,
    trainingRows,
    jobCategoryRows,
    lookups,
    savingTraining,
    savingJobCategories,
  ]);

  if (!isAdminPlus) return null;

  return (
    <div style={{ marginTop: 24, borderTop: "1px solid #d0d0d0", paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: "0 0 6px", fontSize: 20 }}>Matrix Builder</h3>
          <p style={{ margin: 0, fontSize: 13, color: "#555" }}>
            Admin+ only: manage required training and category matrices.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {expanded && (
            <Button variant="outlined" size="small" onClick={loadMatrixData} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          )}
          <Button variant="contained" size="small" onClick={handleExpandToggle}>
            {expanded ? "Collapse Matrix Builder" : "Open Matrix Builder"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <Button
              size="small"
              variant={activeTab === "training" ? "contained" : "outlined"}
              onClick={() => setActiveTab("training")}
            >
              Training Matrix
            </Button>
            <Button
              size="small"
              variant={activeTab === "job-categories" ? "contained" : "outlined"}
              onClick={() => setActiveTab("job-categories")}
            >
              Job Category Matrix
            </Button>
          </div>

          {loading && <p style={{ margin: "0 0 10px" }}>Loading matrix data...</p>}
          {!loading && error && (
            <p style={{ margin: "0 0 10px", color: "#9b1c1c", whiteSpace: "pre-wrap" }}>{error}</p>
          )}
          {!loading && !error && status && (
            <p style={{ margin: "0 0 10px", color: "#0f5132" }}>{status}</p>
          )}

          {!loading && !error && tabContent}
        </div>
      )}
    </div>
  );
}

export default MatrixBuilderPanel;
